import { useEffect, useMemo, useState, useRef, type WheelEvent } from "react";
import {
  ArrowRight,
  Briefcase,
  ChevronDown,
  Clock,
  Edit,
  Home,
  MapPin,
  Navigation,
  Plus,
  Search,
  Shield,
  Star,
  Tag,
  Trash2,
  Utensils,
  Zap,
  Flame,
  Gift,
  Percent,
  X,
} from "lucide-react";
import type { Restaurant } from "../../App";
import { api } from "../../lib/api";
import { resolveAssetUrl } from "../../lib/assets";
import { getRestaurants } from "../../lib/mockData";

interface LandingPageProps {
  onSignInClick: () => void;
  onSignupClick: () => void;
  onBrowseClick: () => void;
  onDineOutClick: () => void;
}

interface LandingOffer {
  id: string;
  code: string;
  title: string;
  description: string;
  discount: string;
  badge: string;
  ctaLabel: string;
  image?: string | null;
  accentColor: string;
  background: string;
  chipBackground: string;
  displayOrder: number;
}

interface LandingFoodCategory {
  id: string;
  label: string;
  image: string;
  bg: string;
}

const FALLBACK_LIGHT_COLORS = [
  "#FFF3E0",
  "#FCE4EC",
  "#E8F5E9",
  "#E3F2FD",
  "#FFF8E1",
  "#EFEBE9",
  "#E0F2F1",
  "#F3E5F5",
  "#F1F8E9",
  "#FBE9E7",
];

const ORDER_ONLINE_FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1489564239502-7a532064e1c2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMHBsYXRlfGVufDF8fHx8MTc2Nzg3MzU0OXww&ixlib=rb-4.1.0&q=80&w=1080",
  "https://images.unsplash.com/photo-1699236290868-8070cd393ba6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXJnZXIlMjBwbGF0ZXxlbnwxfHx8fDE3Njc4NzM1NTB8MA&ixlib=rb-4.1.0&q=80&w=1080",
  "https://images.unsplash.com/photo-1633945274417-ab205ae69d10?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiaXJ5YW5pJTIwcmljZXxlbnwxfHx8fDE3Njc4MzQ5ODl8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1767324672643-c4979362f922?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGluZXNlJTIwbm9vZGxlcyUyMHBsYXRlfGVufDF8fHx8MTc2Nzg3MzU1MHww&ixlib=rb-4.1.0&q=80&w=1080",
  "https://images.unsplash.com/photo-1767114915989-c6ab3c8fc42e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxub3J0aCUyMGluZGlhbiUyMGN1cnJ5fGVufDF8fHx8MTc2Nzg4NDg2NHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1665660710687-b44c50751054?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkb3NhJTIwc291dGglMjBpbmRpYW58ZW58MXx8fHwxNzY3NzkwMTU3fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1703258581842-31608ecd6528?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwYXN0YSUyMGl0YWxpYW58ZW58MXx8fHwxNzY3ODQ3NjkwfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1694850184798-320a8e10bb5e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb21vcyUyMGR1bXBsaW5nc3xlbnwxfHx8fDE3Njc3OTYwNDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1566925950657-6a225149fa90?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzYW5kd2ljaCUyMHBsYXRlfGVufDF8fHx8MTc2Nzc3OTMwNnww&ixlib=rb-4.1.0&q=80&w=1080",
  "https://images.unsplash.com/photo-1730325559618-940c72290ef0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXNoaSUyMGphcGFuZXNlfGVufDF8fHx8MTc2Nzg2ODE5MHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1626323109252-0adb3b46692b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxrZWJhYiUyMHNrZXdlcnN8ZW58MXx8fHwxNzY3ODg0ODY1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1618263655794-f2e728e59521?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb2ZmZWUlMjBiZXZlcmFnZXxlbnwxfHx8fDE3Njc4NTI3Mzd8MA&ixlib=rb-4.1.0&q=80&w=1080",
  "https://images.unsplash.com/photo-1673551493011-2b5f771013d4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpY2UlMjBjcmVhbSUyMGRlc3NlcnR8ZW58MXx8fHwxNzY3Nzk0MDgzfDA&ixlib=rb-4.1.0&q=80&w=1080",
  "https://images.unsplash.com/photo-1550041503-367c95109143?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwYW5jYWtlJTIwYnJlYWtmYXN0fGVufDF8fHx8MTc2NzgwMjAyM3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1640718153995-db4d3f0a6337?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwYm93bCUyMHNhbGFkfGVufDF8fHx8MTc2Nzg3Nzc4M3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1701109876066-7fc0c08da447?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb3VwJTIwYm93bHxlbnwxfHx8fDE3Njc4NTE5MjR8MA&ixlib=rb-4.1.0&q=80&w=1080",
  "https://images.unsplash.com/photo-1670688866261-db6697858df8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmcmllZCUyMGNoaWNrZW4lMjBmYXN0JTIwZm9vZHxlbnwxfHx8fDE3Njc4ODQ4Njd8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1546853846-53490ede35b4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0YWNvcyUyMG1leGljYW58ZW58MXx8fHwxNzY3ODA2NzE1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1669472546359-418a98630699?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzYWxhZCUyMHBsYXRlfGVufDF8fHx8MTc2Nzg3MzU1MXww&ixlib=rb-4.1.0&q=80&w=1080",
];

// Landing card visuals (Food Delivery / Dine Out)
const LANDING_FOOD_DELIVERY_IMAGE =
  "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1600&q=80";
const LANDING_DINE_OUT_IMAGE =
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&auto=format&fit=crop&q=80";

const FOOD_CATEGORIES = [
  { label: "Biryani", emoji: "🍛", bg: "#FFF3E0" },
  { label: "Pizza", emoji: "🍕", bg: "#FCE4EC" },
  { label: "Burger", emoji: "🍔", bg: "#E8F5E9" },
  { label: "Sushi", emoji: "🍱", bg: "#E3F2FD" },
  { label: "Thali", emoji: "🥘", bg: "#F3E5F5" },
  { label: "Noodles", emoji: "🍜", bg: "#FFF8E1" },
  { label: "Desserts", emoji: "🍰", bg: "#FCE4EC" },
  { label: "Rolls", emoji: "🌯", bg: "#E0F2F1" },
  { label: "Coffee", emoji: "☕", bg: "#EFEBE9" },
  { label: "Salad", emoji: "🥗", bg: "#F1F8E9" },
];

const FEATURES = [
  {
    icon: Zap,
    title: "30-min delivery",
    desc: "Lightning-fast delivery to your doorstep, every single time.",
    color: "#FF6B35",
    bg: "#FFF3EE",
  },
  {
    icon: Shield,
    title: "100% safe food",
    desc: "All restaurants are verified & follow strict hygiene standards.",
    color: "#00C853",
    bg: "#E8F5E9",
  },
  {
    icon: Percent,
    title: "Best offers",
    desc: "Exclusive discounts, cashbacks & free delivery on every order.",
    color: "#7C4DFF",
    bg: "#EDE7F6",
  },
  {
    icon: Utensils,
    title: "500+ restaurants",
    desc: "Explore cuisines from top-rated local & cloud kitchens near you.",
    color: "#FF4081",
    bg: "#FCE4EC",
  },
];

const HERO_STATS = [
  { value: "5L+", label: "Happy customers" },
  { value: "500+", label: "Restaurants" },
  { value: "20 min", label: "Avg delivery" },
  { value: "4.8★", label: "App rating" },
];

const DEFAULT_LANDING_OFFERS: LandingOffer[] = [
  {
    id: "offer-first-order",
    code: "FIRST_ORDER",
    title: "Use code: FIRSTORDER",
    description: "Get 50% OFF on your first order (up to Rs 150)",
    discount: "50% OFF",
    badge: "New User Offer",
    ctaLabel: "Claim Now",
    accentColor: "#FC8019",
    background: "linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)",
    chipBackground: "rgba(252,128,25,0.2)",
    image: null,
    displayOrder: 0,
  },
  {
    id: "offer-free-delivery",
    code: "FREE_DELIVERY",
    title: "Free delivery on weekend cravings",
    description: "Order from selected restaurants and skip delivery fees tonight.",
    discount: "FREE DELIVERY",
    badge: "Weekend Special",
    ctaLabel: "Order Now",
    accentColor: "#fbbf24",
    background: "linear-gradient(135deg,#3b0764 0%,#6d28d9 55%,#2563eb 100%)",
    chipBackground: "rgba(251,191,36,0.2)",
    image: null,
    displayOrder: 1,
  },
  {
    id: "offer-combo",
    code: "COMBO_DEAL",
    title: "Flat 30% OFF on combo meals",
    description: "Big savings on curated meal combos from top-rated kitchens.",
    discount: "30% OFF",
    badge: "Limited Time",
    ctaLabel: "View Deals",
    accentColor: "#fde047",
    background: "linear-gradient(135deg,#14532d 0%,#15803d 45%,#166534 100%)",
    chipBackground: "rgba(253,224,71,0.18)",
    image: null,
    displayOrder: 2,
  },
];

const OFFER_THEME = {
  background: "linear-gradient(135deg,#FC8019 0%,#E84545 100%)",
  accentColor: "#ffffff",
  chipBackground: "rgba(255,255,255,0.18)",
};

const DEFAULT_DYNAMIC_FOOD_CATEGORIES: LandingFoodCategory[] = [
  {
    id: "biryani",
    label: "Biryani",
    image: "https://images.unsplash.com/photo-1633945274417-ab205ae69d10?auto=format&fit=crop&w=400&q=80",
    bg: "#FFF3E0",
  },
  {
    id: "pizza",
    label: "Pizza",
    image: "https://images.unsplash.com/photo-1489564239502-7a532064e1c2?auto=format&fit=crop&w=400&q=80",
    bg: "#FCE4EC",
  },
  {
    id: "burger",
    label: "Burger",
    image: "https://images.unsplash.com/photo-1699236290868-8070cd393ba6?auto=format&fit=crop&w=400&q=80",
    bg: "#E8F5E9",
  },
  {
    id: "sushi",
    label: "Sushi",
    image: "https://images.unsplash.com/photo-1730325559618-940c72290ef0?auto=format&fit=crop&w=400&q=80",
    bg: "#E3F2FD",
  },
  {
    id: "desserts",
    label: "Desserts",
    image: "https://images.unsplash.com/photo-1673551493011-2b5f771013d4?auto=format&fit=crop&w=400&q=80",
    bg: "#FFF8E1",
  },
  {
    id: "coffee",
    label: "Coffee",
    image: "https://images.unsplash.com/photo-1618263655794-f2e728e59521?auto=format&fit=crop&w=400&q=80",
    bg: "#EFEBE9",
  },
];


const CATEGORY_VISUALS: Array<{ match: string[]; image: string; bg: string }> = [
  {
    match: ["pizza", "italian", "pasta"],
    image: "https://images.unsplash.com/photo-1489564239502-7a532064e1c2?auto=format&fit=crop&w=400&q=80",
    bg: "#FCE4EC",
  },
  {
    match: ["burger", "fast food", "american"],
    image: "https://images.unsplash.com/photo-1699236290868-8070cd393ba6?auto=format&fit=crop&w=400&q=80",
    bg: "#E8F5E9",
  },
  {
    match: ["biryani", "indian", "mughlai"],
    image: "https://images.unsplash.com/photo-1633945274417-ab205ae69d10?auto=format&fit=crop&w=400&q=80",
    bg: "#FFF3E0",
  },
  {
    match: ["chinese", "noodles", "asian"],
    image: "https://images.unsplash.com/photo-1767324672643-c4979362f922?auto=format&fit=crop&w=400&q=80",
    bg: "#FFF8E1",
  },
  {
    match: ["south indian", "dosa"],
    image: "https://images.unsplash.com/photo-1665660710687-b44c50751054?auto=format&fit=crop&w=400&q=80",
    bg: "#E0F2F1",
  },
  {
    match: ["sushi", "japanese"],
    image: "https://images.unsplash.com/photo-1730325559618-940c72290ef0?auto=format&fit=crop&w=400&q=80",
    bg: "#E3F2FD",
  },
  {
    match: ["coffee", "beverage"],
    image: "https://images.unsplash.com/photo-1618263655794-f2e728e59521?auto=format&fit=crop&w=400&q=80",
    bg: "#EFEBE9",
  },
  {
    match: ["dessert", "ice cream", "bakery"],
    image: "https://images.unsplash.com/photo-1673551493011-2b5f771013d4?auto=format&fit=crop&w=400&q=80",
    bg: "#FCE4EC",
  },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value || 0);

const LOCATION_PLACEHOLDER = "Select Location";
const NEARBY_RADIUS_KM = 3;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const calculateDistanceKm = (
  startLat?: number,
  startLng?: number,
  endLat?: number,
  endLng?: number
) => {
  if (
    !Number.isFinite(startLat) ||
    !Number.isFinite(startLng) ||
    !Number.isFinite(endLat) ||
    !Number.isFinite(endLng)
  ) {
    return null;
  }

  const dLat = toRadians((endLat as number) - (startLat as number));
  const dLng = toRadians((endLng as number) - (startLng as number));
  const lat1 = toRadians(startLat as number);
  const lat2 = toRadians(endLat as number);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return Number((6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2));
};

const parseJsonObject = (value: unknown) => {
  if (!value || typeof value !== "string") return {};

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
};

const looksLikeImageSource = (value?: string | null) => {
  const source = String(value || "").trim();
  if (!source) return false;

  return (
    source.startsWith("http://") ||
    source.startsWith("https://") ||
    source.startsWith("data:image/") ||
    source.startsWith("/") ||
    /\.(png|jpe?g|webp|gif|svg|avif)$/i.test(source)
  );
};

const looksLikeColor = (value?: string | null) => {
  const color = String(value || "").trim();
  if (!color) return false;

  return (
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color) ||
    /^rgba?\(/i.test(color) ||
    /^hsla?\(/i.test(color)
  );
};

const mapCuisineToLandingCategory = (item: any, index: number): LandingFoodCategory => {
  const id = String(item?.id || item?.code || item?.name || `category-${index}`);
  const label = String(item?.name || item?.code || "Cuisine").trim();
  const lookup = `${String(item?.code || "")} ${label}`.toLowerCase();
  const metadata = parseJsonObject(item?.metadata);
  const sequentialFallbackImage =
    ORDER_ONLINE_FALLBACK_IMAGES[index % ORDER_ONLINE_FALLBACK_IMAGES.length] || FALLBACK_IMAGE;
  const sequentialFallbackColor =
    FALLBACK_LIGHT_COLORS[index % FALLBACK_LIGHT_COLORS.length] || "#FFF3E0";
  const cuisineImageCandidate =
    resolveAssetUrl(item?.image) ||
    resolveAssetUrl(item?.icon) ||
    resolveAssetUrl(typeof metadata.image === "string" ? metadata.image : "") ||
    resolveAssetUrl(typeof metadata.icon === "string" ? metadata.icon : "") ||
    String(
      item?.image ||
        item?.icon ||
        (typeof metadata.image === "string" ? metadata.image : "") ||
        (typeof metadata.icon === "string" ? metadata.icon : "") ||
        ""
    ).trim();
  const cuisineImage = looksLikeImageSource(cuisineImageCandidate) ? cuisineImageCandidate : "";
  const colorCandidate =
    String(
      item?.lightColor ||
        item?.light_color ||
        item?.bg ||
        item?.bgColor ||
        item?.background ||
        item?.backgroundColor ||
        item?.color ||
        item?.cardColor ||
        item?.cardBackground ||
        (typeof metadata.lightColor === "string" ? metadata.lightColor : "") ||
        (typeof metadata.light_color === "string" ? metadata.light_color : "") ||
        (typeof metadata.bg === "string" ? metadata.bg : "") ||
        (typeof metadata.bgColor === "string" ? metadata.bgColor : "") ||
        (typeof metadata.background === "string" ? metadata.background : "") ||
        (typeof metadata.backgroundColor === "string" ? metadata.backgroundColor : "") ||
        (typeof metadata.color === "string" ? metadata.color : "") ||
        ""
    ).trim();
  const cuisineBg = looksLikeColor(colorCandidate) ? colorCandidate : "";

  return {
    id,
    label,
    image: cuisineImage || sequentialFallbackImage,
    bg: cuisineBg || sequentialFallbackColor,
  };
};

const getCategorySortValue = (item: any, fallbackIndex: number) => {
  const rawOrder = item?.displayOrder ?? item?.display_order ?? item?.sortOrder ?? item?.sort_order;
  const numericOrder = Number(rawOrder);
  return Number.isFinite(numericOrder) ? numericOrder : fallbackIndex;
};

const resolveImage = (r: Restaurant): string => {
  const candidates = [r.coverImage, r.banner, r.logo];
  for (const c of candidates) {
    const resolved = resolveAssetUrl(c) || String(c || "").trim();
    if (resolved) return resolved;
  }
  return FALLBACK_IMAGE;
};

// ─── Pill badge ────────────────────────────────────────────────────────────────
const OfferBadge = ({ text }: { text: string }) => (
  <span
    style={{
      background: "linear-gradient(90deg,#FF6B35,#FF4757)",
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      padding: "3px 10px",
      borderRadius: 20,
      letterSpacing: 0.3,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    }}
  >
    <Tag size={10} />
    {text}
  </span>
);

// ─── Restaurant Card (Order Online style) ────────────────────────────────────
const RestaurantCard = ({
  restaurant,
  index,
  onClick,
}: {
  restaurant: Restaurant & { image: string; offerText: string };
  index: number;
  onClick: (restaurant: Restaurant) => void;
}) => {
  const [imgError, setImgError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const isOpen = restaurant.isOpen !== false;

  // Filter out UUID-looking strings from cuisine array
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const cleanCuisines = restaurant.cuisine.filter((c) => !uuidRegex.test(c));

  return (
    <article
      onClick={() => onClick(restaurant)}
      style={{
        background: "#fff",
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        cursor: "pointer",
        transition: "transform 0.22s ease, box-shadow 0.22s ease",
        border: "1px solid #f0f0f0",
        display: "flex",
        flexDirection: "column",
        animationDelay: `${index * 60}ms`,
        opacity: isOpen ? 1 : 0.65,
        filter: isOpen ? "none" : "grayscale(60%)",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.transform = "translateY(-5px)";
        (e.currentTarget as HTMLElement).style.boxShadow = "0 12px 36px rgba(0,0,0,0.13)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
        (e.currentTarget as HTMLElement).style.boxShadow = "0 2px 16px rgba(0,0,0,0.07)";
      }}
    >
      {/* Banner image */}
      <div style={{ position: "relative", height: 180, background: "#f0f0f0", overflow: "hidden" }}>
        <img
          src={imgError ? FALLBACK_IMAGE : restaurant.image}
          alt={restaurant.name}
          onError={() => setImgError(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
          onMouseEnter={(e) => { (e.target as HTMLImageElement).style.transform = "scale(1.05)"; }}
          onMouseLeave={(e) => { (e.target as HTMLImageElement).style.transform = "scale(1)"; }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)" }} />

        {/* Online/Offline badge */}
        <div style={{
          position: "absolute", top: 12, left: 12,
          background: isOpen ? "#16a34a" : "#dc2626",
          color: "#fff", borderRadius: 20,
          padding: "4px 12px", fontSize: 12, fontWeight: 700,
          fontFamily: "'Manrope', 'Segoe UI', sans-serif",
        }}>
          {isOpen ? "Online" : "Offline"}
        </div>

        {/* Offer badge */}
        {restaurant.hasOffers && (
          <div style={{
            position: "absolute", top: isOpen ? 44 : 12, left: 12,
            background: "linear-gradient(90deg,#FC8019,#E84545)",
            color: "#fff", borderRadius: 20,
            padding: "3px 10px", fontSize: 11, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 4,
            fontFamily: "'Manrope', 'Segoe UI', sans-serif",
          }}>
            <Tag size={10} />
            {restaurant.offerText}
          </div>
        )}

        {/* Heart / Favorite */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsFav(!isFav); }}
          style={{
            position: "absolute", top: 12, right: 12,
            width: 34, height: 34, borderRadius: "50%",
            background: "#fff", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            transition: "transform 0.18s",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1.15)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill={isFav ? "#FC8019" : "none"} stroke={isFav ? "#FC8019" : "#555"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>

        {/* Star rating */}
        <div style={{
          position: "absolute", bottom: 12, left: 12,
          background: "#fff", borderRadius: 20,
          padding: "4px 10px", fontSize: 13, fontWeight: 700,
          display: "flex", alignItems: "center", gap: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          color: "#1a1a1a",
          fontFamily: "'Manrope', 'Segoe UI', sans-serif",
        }}>
          <Star size={13} fill="#FC8019" color="#FC8019" />
          {restaurant.rating?.toFixed(1)}
        </div>

        {/* Distance */}
        {restaurant.distance && (
          <div style={{
            position: "absolute", bottom: 12, right: 12,
            background: "rgba(37,99,235,0.92)", borderRadius: 20,
            padding: "4px 10px", fontSize: 12, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 4,
            color: "#fff",
            fontFamily: "'Manrope', 'Segoe UI', sans-serif",
          }}>
            <Navigation size={12} />
            {restaurant.distance} km away
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: "14px 16px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Logo + Name + Description */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
          <img
            src={logoError ? FALLBACK_IMAGE : (restaurant.logo || restaurant.image)}
            alt={restaurant.name}
            onError={() => setLogoError(true)}
            style={{
              width: 48, height: 48, borderRadius: 10,
              objectFit: "cover", border: "1.5px solid #f0f0f0",
              flexShrink: 0, boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{
              fontSize: 16, fontWeight: 700, color: "#1a1a1a", margin: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              fontFamily: "'Manrope', 'Segoe UI', sans-serif",
            }}>
              {restaurant.name}
            </h3>
            <p style={{
              fontSize: 13, color: "#686b78", marginTop: 3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              fontFamily: "'Manrope', 'Segoe UI', sans-serif",
            }}>
              {restaurant.description || "Fresh food delivered fast"}
            </p>
          </div>
        </div>

        {/* Cuisine chip tags */}
        {cleanCuisines.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {cleanCuisines.slice(0, 3).map((c) => (
              <span key={c} style={{
                background: "#EFF6FF", color: "#1d4ed8",
                borderRadius: 20, padding: "3px 10px",
                fontSize: 12, fontWeight: 600,
                fontFamily: "'Manrope', 'Segoe UI', sans-serif",
              }}>
                {c}
              </span>
            ))}
            {cleanCuisines.length > 3 && (
              <span style={{
                background: "#f3f4f6", color: "#6b7280",
                borderRadius: 20, padding: "3px 10px",
                fontSize: 12, fontWeight: 600,
                fontFamily: "'Manrope', 'Segoe UI', sans-serif",
              }}>
                +{cleanCuisines.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Delivery info */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          paddingTop: 10, borderTop: "1px solid #f2f2f2",
          marginTop: "auto",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#16a34a", fontSize: 13, fontWeight: 600 }}>
            <Clock size={13} color="#16a34a" />
            {restaurant.deliveryTime}
          </div>
          <div style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#FC8019" }}>
            ₹{restaurant.deliveryFee === 0 ? "0" : restaurant.deliveryFee}
          </div>
        </div>
      </div>
    </article>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
export function LandingPage({
  onSignInClick,
  onSignupClick,
  onBrowseClick,
  onDineOutClick,
}: LandingPageProps) {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [offers, setOffers] = useState<LandingOffer[]>(DEFAULT_LANDING_OFFERS);
  const [foodCategories, setFoodCategories] = useState<LandingFoodCategory[]>(DEFAULT_DYNAMIC_FOOD_CATEGORIES);
  const [activeOfferIndex, setActiveOfferIndex] = useState(0);
  const [offerPaused, setOfferPaused] = useState(false);
  const categoriesRef = useRef<HTMLDivElement>(null);

  // ── Location state ──────────────────────────────────────────────────────────
  const [selectedLocation, setSelectedLocation] = useState(LOCATION_PLACEHOLDER);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [locationAccessError, setLocationAccessError] = useState<string | null>(null);
  const [savedAddresses, setSavedAddresses] = useState<
    { id: string; type: string; address: string; lat?: number; lng?: number }[]
  >([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const hasSelectedLocation = selectedLocation !== LOCATION_PLACEHOLDER;

  // Sticky header on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadOffers = async () => {
      try {
        const response = await api.masterData.offerCategories.getLandingOffers();
        const rows = Array.isArray(response?.data) ? response.data : [];
        if (!mounted || rows.length === 0) return;
        setOffers(rows);
      } catch {
        if (!mounted) return;
        setOffers(DEFAULT_LANDING_OFFERS);
      }
    };

    void loadOffers();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadFoodCategories = async () => {
      try {
        const response = await api.masterData.cuisineTypes.getAll();
        const rows = Array.isArray(response?.data) ? response.data : [];
        if (!mounted || rows.length === 0) return;

        const nextCategories = rows
          .filter((item: any) => item?.isActive !== false)
          .sort((a: any, b: any) => {
            const orderA = getCategorySortValue(a, rows.indexOf(a));
            const orderB = getCategorySortValue(b, rows.indexOf(b));
            if (orderA !== orderB) return orderA - orderB;

            const nameA = String(a?.name || a?.code || "").trim().toLowerCase();
            const nameB = String(b?.name || b?.code || "").trim().toLowerCase();
            return nameA.localeCompare(nameB);
          })
          .map(mapCuisineToLandingCategory);

        if (nextCategories.length > 0) {
          setFoodCategories(nextCategories);
        }
      } catch {
        if (!mounted) return;
        setFoodCategories(DEFAULT_DYNAMIC_FOOD_CATEGORIES);
      }
    };

    void loadFoodCategories();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (offers.length <= 1 || offerPaused) return;

    const timer = window.setInterval(() => {
      setActiveOfferIndex((current) => (current + 1) % offers.length);
    }, 3000);

    return () => window.clearInterval(timer);
  }, [offers, offerPaused]);

  useEffect(() => {
    if (activeOfferIndex < offers.length) return;
    setActiveOfferIndex(0);
  }, [activeOfferIndex, offers.length]);



  // ── Location handlers ───────────────────────────────────────────────────────
  const openLocationModal = async () => {
    setShowLocationModal(true);
    setLoadingAddresses(true);
    try {
      const resp = await api.users.getSavedLocations();
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setSavedAddresses(
        rows.map((item: any) => {
          const rawType = String(item.addressType || "HOME").toUpperCase();
          const type =
            rawType === "OFFICE" ? "Work" : rawType === "OTHER" ? "Other" : "Home";
          return {
            id: item.id,
            type,
            address: item.fullAddress,
            lat: typeof item.latitude === "number" ? item.latitude : undefined,
            lng: typeof item.longitude === "number" ? item.longitude : undefined,
          };
        })
      );
    } catch {
      /* skip – user not logged in or network error */
    } finally {
      setLoadingAddresses(false);
    }
  };

  const detectAndSelectCurrentLocation = (options?: { closeModal?: boolean }) => {
    const closeModal = options?.closeModal ?? false;

    setLocationAccessError(null);
    setDetectingLocation(true);

    if (!navigator.geolocation) {
      setDetectingLocation(false);
      setLocationAccessError("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
          .then((r) => r.json())
          .then((data) => {
            const addr =
              data?.display_name || `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`;
            setUserCoords({ lat: latitude, lng: longitude });
            setSelectedLocation(addr);
            try {
              localStorage.setItem('customer_latitude', String(latitude));
              localStorage.setItem('customer_longitude', String(longitude));
              localStorage.setItem('customer_location_label', String(addr));
            } catch {
              // ignore storage errors
            }
            if (closeModal) setShowLocationModal(false);
            setLocationAccessError(null);
            setDetectingLocation(false);
          })
          .catch(() => {
            setUserCoords({ lat: latitude, lng: longitude });
            const fallbackLabel = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
            setSelectedLocation(fallbackLabel);
            try {
              localStorage.setItem('customer_latitude', String(latitude));
              localStorage.setItem('customer_longitude', String(longitude));
              localStorage.setItem('customer_location_label', fallbackLabel);
            } catch {
              // ignore storage errors
            }
            if (closeModal) setShowLocationModal(false);
            setLocationAccessError(null);
            setDetectingLocation(false);
          });
      },
      (err) => {
        setDetectingLocation(false);
        if (err?.code === 1) {
          // NOTE: When the permission is denied/blocked, browsers will NOT show the native prompt again
          // until the user resets the site permission from the address bar (🔒/ⓘ icon).
          setLocationAccessError("Location permission is blocked. Please allow it from the browser address bar.");
          return;
        }
        setLocationAccessError("Could not detect location. Please try again.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleUseCurrentLocation = () => {
    detectAndSelectCurrentLocation({ closeModal: true });
  };

  // Auto-request live location on landing page load.
  useEffect(() => {
    const tryAuto = async () => {
      try {
        const perms = (navigator as any)?.permissions;
        if (perms?.query) {
          const status = await perms.query({ name: "geolocation" });
          if (status?.state === "denied") return;
        }
      } catch {
        // ignore permissions API errors
      }
      detectAndSelectCurrentLocation({ closeModal: false });
    };

    void tryAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectSavedAddress = (addr: {
    id: string;
    type: string;
    address: string;
    lat?: number;
    lng?: number;
  }) => {
    if (addr.lat && addr.lng) {
      setUserCoords({ lat: addr.lat, lng: addr.lng });
    } else {
      setUserCoords(null);
    }
    setSelectedLocation(addr.address);
    setShowLocationModal(false);
    try {
      localStorage.setItem('customer_location_label', String(addr.address));
      if (addr.lat && addr.lng) {
        localStorage.setItem('customer_latitude', String(addr.lat));
        localStorage.setItem('customer_longitude', String(addr.lng));
      }
    } catch {
      // ignore storage errors
    }
  };

  const persistCustomerLocation = () => {
    try {
      if (selectedLocation && selectedLocation !== LOCATION_PLACEHOLDER) {
        localStorage.setItem('customer_location_label', String(selectedLocation));
      }
      if (userCoords) {
        localStorage.setItem('customer_latitude', String(userCoords.lat));
        localStorage.setItem('customer_longitude', String(userCoords.lng));
      }
    } catch {
      // ignore storage errors
    }
  };

  const goToCustomerDiscovery = () => {
    persistCustomerLocation();
    onBrowseClick();
  };

  const goToDineOutDiscovery = () => {
    persistCustomerLocation();
    onDineOutClick();
  };

  // Load restaurants
  useEffect(() => {
    let mounted = true;

    const mapRestaurant = (row: any, index: number): Restaurant => {
      const deliveryRaw = String(
        row?.deliveryTime ?? row?.delivery_time ?? "25-35 mins",
      ).trim();
      const deliveryTime = deliveryRaw.includes("min")
        ? deliveryRaw
        : `${deliveryRaw} mins`;
      const cuisineSource = Array.isArray(row?.cuisine)
        ? row.cuisine
        : String(row?.cuisine ?? row?.cuisineType ?? "")
            .split(",")
            .map((item: string) => item.trim())
            .filter(Boolean);

      return {
        id: String(row?.id ?? `r-${index}`),
        name: String(row?.name ?? "Featured Restaurant"),
        description: String(row?.description ?? "Fresh meals, delivered fast."),
        logo: String(row?.logo ?? ""),
        banner: String(row?.banner ?? row?.coverImage ?? ""),
        coverImage: String(
          row?.coverImage ?? row?.cover_image ?? row?.banner ?? "",
        ),
        address: String(row?.address ?? ""),
        city: String(row?.city ?? ""),
        phone: String(row?.phone ?? ""),
        email: String(row?.email ?? ""),
        latitude:
          typeof row?.latitude === "number"
            ? row.latitude
            : typeof row?.lat === "number"
              ? row.lat
              : undefined,
        longitude:
          typeof row?.longitude === "number"
            ? row.longitude
            : typeof row?.lng === "number"
              ? row.lng
              : undefined,
        primaryColor: String(row?.primaryColor ?? "#FC8019"),
        secondaryColor: String(row?.secondaryColor ?? "#fff7ed"),
        isActive: Boolean(row?.isActive ?? row?.is_active ?? true),
        rating: Number(row?.rating ?? 4.4) || 4.4,
        totalOrders: Number(row?.totalOrders ?? 0) || 0,
        cuisine: cuisineSource.length ? cuisineSource : ["Multi-cuisine"],
        openTime: String(row?.openTime ?? "10:00"),
        closeTime: String(row?.closeTime ?? "23:00"),
        deliveryTime,
        minimumOrder: Number(row?.minimumOrder ?? 199) || 199,
        deliveryFee: Number(row?.deliveryFee ?? 0) || 0,
        freeDeliveryAbove: Number(row?.freeDeliveryAbove ?? 499) || 499,
        taxRate: Number(row?.taxRate ?? 5) || 5,
        packagingCharges: Number(row?.packagingCharges ?? 0) || 0,
        distance: Number(
          row?.distance ?? (index + 1.5 + Math.random()).toFixed(1),
        ),
        isPureVeg: Boolean(row?.isPureVeg ?? false),
        isOpen: row?.isOpen ?? true,
        hasOffers: row?.hasOffers ?? true,
      };
    };

    const load = async () => {
      if (!userCoords) {
        setRestaurants([]);
        setIsLoading(false);
        return;
      }

      try {
        const nearbyResp = await api.restaurants.getNearby({
          latitude: userCoords.lat,
          longitude: userCoords.lng,
          radiusKm: NEARBY_RADIUS_KM,
          limit: 8,
        });
        if (!mounted) return;
        const nearbyLive = Array.isArray(nearbyResp?.data)
          ? nearbyResp.data.map(mapRestaurant).filter((r: Restaurant) => r.isActive)
          : [];
        setRestaurants(nearbyLive.slice(0, 8));
        return;
      } catch {
        /* fallback below */
      }

      if (!mounted) return;

      const fallback = getRestaurants()
        .map((restaurant) => {
          const distanceKm =
            calculateDistanceKm(
              userCoords.lat,
              userCoords.lng,
              restaurant.latitude,
              restaurant.longitude
            ) ?? restaurant.distance ?? null;

          return distanceKm === null
            ? restaurant
            : { ...restaurant, distance: Number(distanceKm.toFixed(1)) };
        })
        .filter(
          (restaurant) =>
            restaurant.isActive &&
            typeof restaurant.distance === "number" &&
            restaurant.distance <= NEARBY_RADIUS_KM
        )
        .sort(
          (a, b) =>
            (a.distance ?? Number.MAX_SAFE_INTEGER) -
            (b.distance ?? Number.MAX_SAFE_INTEGER)
        )
        .slice(0, 8);

      setRestaurants(fallback);
    };

    setIsLoading(true);
    void load().finally(() => {
      if (mounted) setIsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [userCoords]);

  const enriched = useMemo(
    () =>
      restaurants
        .filter((r) => r.isOpen !== false)
        .map((r, i) => ({
          ...r,
          image: resolveImage(r),
          offerText: r.hasOffers
            ? i % 3 === 0
              ? "50% OFF up to ₹100"
              : i % 3 === 1
                ? "FREE delivery"
                : "20% OFF"
            : "Top Rated",
        })),
    [restaurants],
  );

  const handleCategoryWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!categoriesRef.current) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    event.preventDefault();
    categoriesRef.current.scrollBy({
      left: event.deltaY,
      behavior: "smooth",
    });
  };

  const browseWithSearch = (value: string) => {
    const query = String(value || '').trim();
    if (query) {
      sessionStorage.setItem('customer_discovery_cuisine', query);
    }
    goToCustomerDiscovery();
  };

  const browseRestaurant = (restaurant: Restaurant) => {
    try {
      sessionStorage.setItem('post_login_selected_restaurant', JSON.stringify(restaurant));
    } catch {
      // ignore storage errors
    }
    persistCustomerLocation();
    onBrowseClick();
  };

  return (
    <>
      {/* Font & base styles */}
      <style>{`
        * { box-sizing: border-box; }

        .lp-root {
          font-family: 'Manrope', 'Segoe UI', sans-serif;
          background:
            radial-gradient(circle at 28% 6%, rgba(252,128,25,0.12) 0%, rgba(252,128,25,0.0) 58%),
            radial-gradient(circle at 76% 22%, rgba(255,206,165,0.28) 0%, rgba(255,206,165,0.0) 60%),
            radial-gradient(circle at 12% 78%, rgba(232,69,69,0.06) 0%, rgba(232,69,69,0.0) 62%),
            linear-gradient(180deg, #fbf6ee 0%, #fff7f0 38%, #ffffff 100%);
          color: #1a1a1a;
          min-height: 100vh;
          overflow-x: hidden;
        }

        .lp-btn-primary {
          background: linear-gradient(135deg, #FC8019 0%, #E84545 100%);
          color: #fff;
          border: none;
          cursor: pointer;
          font-family: 'Manrope', 'Segoe UI', sans-serif;
          font-weight: 800;
          transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
        }
        .lp-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(252,128,25,0.38); }
        .lp-btn-primary:active { transform: translateY(0); opacity: 0.9; }

        .lp-btn-soft {
          background: rgba(252,128,25,0.14);
          color: #FC8019;
          border: none;
          cursor: pointer;
          font-family: 'Manrope', 'Segoe UI', sans-serif;
          font-weight: 800;
          transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
        }
        .lp-btn-soft:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(252,128,25,0.18); }
        .lp-btn-soft:active { transform: translateY(0); opacity: 0.9; }

        .lp-btn-outline {
          background: transparent;
          color: #FC8019;
          border: 2px solid #FC8019;
          cursor: pointer;
          font-family: 'Manrope', 'Segoe UI', sans-serif;
          font-weight: 700;
          transition: background 0.16s ease, color 0.16s ease, transform 0.16s ease;
        }
        .lp-btn-outline:hover { background: #FC8019; color: #fff; transform: translateY(-2px); }

        .lp-hero-bg {
          background: linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.0) 60%);
          position: relative;
          overflow: hidden;
        }

        .lp-mode-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 22px;
          width: 100%;
          max-width: 100%;
          margin: 0;
        }

        @media (max-width: 900px) {
          .lp-mode-grid {
            grid-template-columns: 1fr;
          }
        }

        .lp-search {
          width: 100%;
          max-width: 640px;
          margin-top: 18px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 999px;
          background: rgba(255,255,255,0.95);
          border: 1px solid rgba(15,23,42,0.10);
          box-shadow: 0 14px 40px rgba(15,23,42,0.14);
        }

        .lp-search input {
          border: none;
          outline: none;
          font-size: 14px;
          font-weight: 650;
          color: #111827;
          background: transparent;
          width: 100%;
          font-family: 'Manrope', 'Segoe UI', sans-serif;
        }

        .lp-mode-card {
          border-radius: 28px;
          overflow: hidden;
          background: rgba(255,255,255,0.6);
          border: 1px solid rgba(15,23,42,0.08);
          box-shadow: 0 22px 60px rgba(15,23,42,0.16);
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
          backdrop-filter: blur(14px);
        }

        .lp-mode-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 28px 70px rgba(15,23,42,0.18);
        }

        .lp-mode-media {
          position: relative;
          height: 320px;
          border-radius: 26px;
          overflow: hidden;
          margin: 14px;
          background: #f3f4f6;
        }

        .lp-mode-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform: scale(1.02);
        }

        .lp-mode-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.0) 100%);
        }

        .lp-mode-content {
          position: absolute;
          left: 16px;
          right: 16px;
          bottom: 16px;
          color: #fff;
          text-shadow: 0 10px 26px rgba(0,0,0,0.35);
        }

        .lp-mode-title {
          margin: 0 0 6px;
          font-size: 26px;
          font-weight: 900;
          letter-spacing: 0.2px;
        }

        .lp-mode-subtitle {
          margin: 0 0 12px;
          font-size: 13px;
          font-weight: 700;
          color: rgba(255,255,255,0.92);
        }

        .lp-mode-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 12px;
        }

        .lp-mode-pill {
          font-size: 11px;
          font-weight: 900;
          color: rgba(255,255,255,0.95);
          background: rgba(255,255,255,0.18);
          border: 1px solid rgba(255,255,255,0.20);
          padding: 6px 10px;
          border-radius: 999px;
          backdrop-filter: blur(10px);
        }

        .lp-mode-btn {
          border: none;
          cursor: pointer;
          width: 100%;
          background: linear-gradient(180deg,#FC8019 0%, #F97316 100%);
          color: #fff;
          padding: 12px 18px;
          border-radius: 14px;
          font-weight: 900;
          box-shadow: 0 12px 26px rgba(252,128,25,0.32);
          font-family: 'Manrope', 'Segoe UI', sans-serif;
        }

        /* Landing "Food Delivery / Dine Out" compact cards (ref screenshot) */
        .lp-mini-card {
          position: relative;
          height: 230px;
          border-radius: 26px;
          overflow: hidden;
          cursor: pointer;
          box-shadow: 0 18px 50px rgba(15,23,42,0.14);
          border: 1px solid rgba(15,23,42,0.08);
          background: #f3f4f6;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .lp-mini-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 24px 62px rgba(15,23,42,0.18);
        }
        .lp-mini-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform: scale(1.03);
        }
        .lp-mini-overlay {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.28) 55%, rgba(0,0,0,0.66) 100%),
            radial-gradient(circle at 18% 75%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 58%);
        }
        .lp-mini-content {
          position: absolute;
          inset: 0;
          padding: 18px 18px 16px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          gap: 10px;
          color: #fff;
          text-shadow: 0 10px 26px rgba(0,0,0,0.40);
        }
        .lp-mini-title {
          font-size: 18px;
          font-weight: 900;
          letter-spacing: 0.2px;
          margin: 0;
          line-height: 1.1;
        }
        .lp-mini-subtitle {
          font-size: 11px;
          font-weight: 700;
          color: rgba(255,255,255,0.92);
          margin: 0;
          line-height: 1.35;
          max-width: 320px;
        }
        .lp-mini-btn {
          align-self: flex-start;
          border: none;
          cursor: pointer;
          padding: 9px 16px;
          border-radius: 12px;
          font-weight: 900;
          font-size: 12px;
          color: #fff;
          background: linear-gradient(135deg, #FC8019 0%, #F97316 55%, #E84545 100%);
          box-shadow: 0 12px 26px rgba(252,128,25,0.34);
          font-family: 'Manrope', 'Segoe UI', sans-serif;
        }
        .lp-mini-btn:hover { filter: brightness(1.02); }

        @media (max-width: 900px) {
          .lp-mini-card { height: 210px; }
          .lp-mini-title { font-size: 18px; }
        }
        .lp-hero-bg::before {
          content: '';
          position: absolute;
          width: 600px; height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(252,128,25,0.12) 0%, transparent 70%);
          top: -200px; right: -100px;
          pointer-events: none;
        }
        .lp-hero-bg::after {
          content: '';
          position: absolute;
          width: 400px; height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(232,69,69,0.08) 0%, transparent 70%);
          bottom: -100px; left: -50px;
          pointer-events: none;
        }

        .lp-search-box:focus-within {
          box-shadow: 0 0 0 3px rgba(252,128,25,0.25);
        }

        .lp-cat-scroll {
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          scroll-behavior: smooth;
        }
        .lp-cat-scroll::-webkit-scrollbar { display: none; }

        .lp-cat-chip {
          flex-shrink: 0;
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
          border: 1.5px solid transparent;
        }
        .lp-cat-chip:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.1);
          border-color: #FC8019;
        }

        .lp-feature-card {
          transition: transform 0.22s ease, box-shadow 0.22s ease;
        }
        .lp-feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 40px rgba(0,0,0,0.1);
        }

        .lp-fade-in {
          animation: lpFadeIn 0.5s ease both;
        }
        @keyframes lpFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .lp-pulse-ring {
          animation: lpPulse 2s ease-in-out infinite;
        }
        @keyframes lpPulse {
          0%,100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.06); opacity: 0.85; }
        }

        .lp-scroll-btn {
          background: #fff;
          border: 1.5px solid #eee;
          border-radius: 50%;
          width: 38px; height: 38px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
          transition: background 0.16s, border-color 0.16s;
          flex-shrink: 0;
        }
        .lp-scroll-btn:hover { background: #FC8019; border-color: #FC8019; color: #fff; }

        .lp-location-wrap:hover .lp-location-tooltip {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }

        .lp-signin-btn {
          white-space: nowrap;
        }

        .lp-signin-btn--mobile {
          display: none;
        }

        @media (max-width: 640px) {
          .lp-header-inner {
            padding: 10px 12px !important;
            gap: 10px !important;
            flex-wrap: wrap;
          }

          .lp-header-logo span {
            font-size: 18px !important;
          }

          .lp-signin-btn--desktop {
            display: none !important;
          }

          .lp-signin-btn--mobile {
            display: inline-flex !important;
            margin-left: auto;
          }

          .lp-header-right {
            width: 100%;
            flex-wrap: wrap;
            justify-content: flex-end;
            gap: 8px !important;
          }

          .lp-signin-btn {
            padding: 7px 12px !important;
            font-size: 12px !important;
          }

          .lp-location-wrap {
            flex: 1 1 100%;
            width: 100%;
          }

          .lp-location-btn {
            width: 100% !important;
            min-width: 0 !important;
            max-width: none !important;
            padding: 7px 12px 7px 10px !important;
          }
        }

        .lp-offer-slider {
          overflow: hidden;
          width: 100%;
        }

        .lp-offer-track {
          display: flex;
          transition: transform 0.55s ease;
          will-change: transform;
        }

        .lp-offer-slide {
          min-width: 100%;
        }
      `}</style>

      <div className="lp-root">
        {/* ── STICKY HEADER ───────────────────────────────────────── */}
        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            background: scrolled ? "rgba(255,255,255,0.97)" : "transparent",
            backdropFilter: scrolled ? "blur(16px)" : "none",
            boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.08)" : "none",
            transition: "all 0.28s ease",
            borderBottom: scrolled ? "1px solid rgba(0,0,0,0.06)" : "none",
          }}
        >
          <div
            className="lp-header-inner"
            style={{
              maxWidth: 1200,
              margin: "0 auto",
              padding: "14px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            {/* Logo */}
            <div className="lp-header-logo" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: "linear-gradient(135deg,#FC8019,#E84545)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Utensils size={20} color="#fff" />
              </div>
              <span
                style={{
                  fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#1a1a1a",
                  lineHeight: 1,
                }}
              >
                FoodHub
              </span>
            </div>

            {/* Mobile Sign In (keeps FoodHub + Sign In on same row) */}
            <button
              className="lp-btn-outline lp-signin-btn lp-signin-btn--mobile"
              onClick={onSignInClick}
              style={{
                padding: "9px 18px",
                borderRadius: 999,
                fontSize: 13,
              }}
            >
              Sign In
            </button>

            {/* Auth buttons + Location pill on right side */}
            <div className="lp-header-right" style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                className="lp-btn-outline lp-signin-btn lp-signin-btn--desktop"
                onClick={onSignInClick}
                style={{
                  padding: "9px 18px",
                  borderRadius: 999,
                  fontSize: 13,
                }}
              >
                Sign In
              </button>

	              {/* Location pill – clickable, opens modal */}
	              <div style={{ position: "relative" }} className="lp-location-wrap">
	                <button
	                  type="button"
	                  onClick={() => detectAndSelectCurrentLocation({ closeModal: false })}
                    className="lp-location-btn"
	                  style={{
	                    display: "flex",
	                    alignItems: "center",
	                    gap: 8,
                    background: "#fff",
                    border: "1.5px solid #fde0c4",
                    borderRadius: 24,
                    padding: "8px 16px 8px 12px",
                    cursor: "pointer",
                    minWidth: 200,
                    maxWidth: 280,
                    boxShadow: "0 2px 8px rgba(252,128,25,0.1)",
                    transition: "border-color 0.18s, box-shadow 0.18s",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "#FC8019";
                    (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 16px rgba(252,128,25,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "#fde0c4";
                    (e.currentTarget as HTMLElement).style.boxShadow = "0 2px 8px rgba(252,128,25,0.1)";
                  }}
                >
                  <MapPin size={15} color="#FC8019" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                    {/* <div style={{ fontSize: 10, color: "#999", fontWeight: 600, lineHeight: 1, marginBottom: 2 }}>
                      Deliver to
                    </div> */}
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#3d4152",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 180,
                      }}
                    >
                      {selectedLocation.length > 28
                        ? selectedLocation.substring(0, 28) + "…"
                        : selectedLocation}
                    </div>
                  </div>
                  <ChevronDown size={13} color="#FC8019" style={{ flexShrink: 0 }} />
                </button>

                {/* Hover tooltip – full address */}
                {selectedLocation.length > 28 && (
                  <div
                    className="lp-location-tooltip"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      right: 0,
                      background: "#1a1a1a",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "8px 12px",
                      borderRadius: 10,
                      whiteSpace: "normal",
                      maxWidth: 320,
                      lineHeight: 1.5,
                      boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
                      zIndex: 200,
                      pointerEvents: "none",
                      opacity: 0,
                      transform: "translateY(-4px)",
                      transition: "opacity 0.18s ease, transform 0.18s ease",
                    }}
                  >
                    {selectedLocation}
                    {/* Arrow */}
                    <div
                      style={{
                        position: "absolute",
                        top: -5,
                        right: 20,
                        width: 10,
                        height: 10,
                        background: "#1a1a1a",
                        transform: "rotate(45deg)",
                        borderRadius: 2,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* ── HERO SECTION ────────────────────────────────────────── */}
        <section
          className="lp-hero-bg"
          style={{ paddingTop: 54, paddingBottom: 34 }}
        >
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
            <div className="lp-fade-in" style={{ textAlign: "center", maxWidth: 980, margin: "0 auto" }}>
                {/* Announcement pill */}
                <div
                  style={{
                    display: "none",
                    alignItems: "center",
                    gap: 8,
                    background: "#fff",
                    border: "1.5px solid #fde0c4",
                    borderRadius: 24,
                    padding: "6px 14px",
                    fontSize: 13,
                    color: "#FC8019",
                    fontWeight: 700,
                    marginBottom: 20,
                    boxShadow: "0 2px 10px rgba(252,128,25,0.12)",
                  }}
                >
                  <Flame size={14} color="#FC8019" />
                  🎉 Free delivery on your first 3 orders!
                </div>

                <h1
                  style={{
                    fontFamily: "'Georgia', 'Times New Roman', serif",
                    fontSize: "clamp(40px, 5.6vw, 66px)",
                    fontWeight: 500,
                    letterSpacing: -0.4,
                    lineHeight: 1.06,
                    color: "#111827",
                    margin: "0 0 14px",
                  }}
                >
                  Order food. Discover best
                  <br />
                  restaurants. Food Hub it!
                </h1>

                <p
                  style={{
                    fontSize: 16,
                    color: "#6b7280",
                    lineHeight: 1.7,
                    margin: "0 auto 16px",
                    maxWidth: 520,
                    fontWeight: 600,
                  }}
                >
                  Delicious meals delivered to your doorstep
                </p>


                <form
                  className="lp-search"
                  onSubmit={(event) => {
                    event.preventDefault();
                    browseWithSearch(searchQuery);
                  }}
                  style={{ marginLeft: "auto", marginRight: "auto" }}
                >
                  <Search size={18} color="#9CA3AF" style={{ flexShrink: 0 }} />
                  <input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search for restaurants, cuisines or dishes..."
                  />
                </form>
              </div>

              {/* Right: Stats cards */}
              <div
                className="lp-fade-in"
                style={{
                  display: "none",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  animationDelay: "120ms",
                }}
              >
                {HERO_STATS.map((stat, i) => (
                  <div
                    key={stat.label}
                    className="lp-pulse-ring"
                    style={{
                      background: "#fff",
                      borderRadius: 20,
                      padding: "24px 20px",
                      boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                      textAlign: "center",
                      border: "1.5px solid #fde0c4",
                      animationDelay: `${i * 400}ms`,
                    }}
                  >
                    <p
                      style={{
                        fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                        fontSize: 32,
                        fontWeight: 800,
                        color: "#FC8019",
                        margin: 0,
                        lineHeight: 1,
                      }}
                    >
                      {stat.value}
                    </p>
                    <p
                      style={{
                        fontSize: 13,
                        color: "#686b78",
                        margin: "6px 0 0",
                        fontWeight: 600,
                      }}
                    >
                      {stat.label}
                    </p>
                  </div>
                ))}

                <div
                  style={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                  }}
                  onMouseEnter={() => setOfferPaused(true)}
                  onMouseLeave={() => setOfferPaused(false)}
                >
                  <div className="lp-offer-slider">
                    <div
                      className="lp-offer-track"
                      style={{ transform: `translateX(-${activeOfferIndex * 100}%)` }}
                    >
                      {offers.map((offer) => {
                        const offerImage = resolveAssetUrl(offer.image) || String(offer.image || "").trim();

                        return (
                          <div key={offer.id} className="lp-offer-slide">
                            <div
                              style={{
                                background: OFFER_THEME.background,
                                borderRadius: 20,
                                padding: "20px 22px",
                                display: "flex",
                                alignItems: "center",
                                gap: 16,
                                boxShadow: "0 8px 32px rgba(15,52,96,0.25)",
                                minHeight: 110,
                              }}
                            >
                              <div
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 14,
                                  background: OFFER_THEME.chipBackground,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  overflow: "hidden",
                                }}
                              >
                                {offerImage ? (
                                  <img
                                    src={offerImage}
                                    alt={offer.title}
                                    loading="lazy"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                ) : (
                                  <Gift size={24} color={OFFER_THEME.accentColor} />
                                )}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 10px",
                                    borderRadius: 999,
                                    background: OFFER_THEME.chipBackground,
                                    color: OFFER_THEME.accentColor,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                    marginBottom: 10,
                                  }}
                                >
                                  <Percent size={12} />
                                  {offer.badge}
                                </div>
                                <p
                                  style={{
                                    color: OFFER_THEME.accentColor,
                                    fontWeight: 800,
                                    fontSize: 15,
                                    margin: "0 0 4px",
                                  }}
                                >
                                  {offer.title}
                                </p>
                                <p
                                  style={{
                                    color: "rgba(255,255,255,0.78)",
                                    fontSize: 13,
                                    margin: 0,
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {offer.description}
                                </p>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-end",
                                  gap: 10,
                                  flexShrink: 0,
                                }}
                              >
                                <div
                                  style={{
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: 800,
                                    letterSpacing: 0.4,
                                  }}
                                >
                                  {offer.discount}
                                </div>
                                <button
                                  onClick={goToCustomerDiscovery}
                                  style={{
                                    background: "#fff",
                                    color: "#FC8019",
                                    border: "none",
                                    borderRadius: 10,
                                    padding: "8px 16px",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                                  }}
                                >
                                  {offer.ctaLabel}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {offers.length > 1 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      {offers.map((offer, index) => (
                        <button
                          key={offer.id}
                          type="button"
                          aria-label={`Show offer ${index + 1}`}
                          onClick={() => setActiveOfferIndex(index)}
                          style={{
                            width: index === activeOfferIndex ? 26 : 8,
                            height: 8,
                            borderRadius: 999,
                            border: "none",
                            cursor: "pointer",
                            background:
                              index === activeOfferIndex ? "#FC8019" : "rgba(26,26,26,0.15)",
                            transition: "all 0.25s ease",
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
        </section>

        {/* ── FOOD CATEGORIES ─────────────────────────────────────── */}
        {/* Mode Selection Cards */}
        <section style={{ padding: "22px 0 0", background: "transparent" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
            <div className="lp-mode-grid">
              <div
                className="lp-mini-card"
                role="button"
                tabIndex={0}
                onClick={goToCustomerDiscovery}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") goToCustomerDiscovery();
                }}
              >
                <img className="lp-mini-img" src={LANDING_FOOD_DELIVERY_IMAGE} alt="Food Delivery" loading="lazy" />
                <div className="lp-mini-overlay" />
                <div className="lp-mini-content">
                  <div>
                    <div className="lp-mini-title">Food Delivery</div>
                    <p className="lp-mini-subtitle">Order from top restaurants, delivered in 30 mins flat</p>
                  </div>
                  <button
                    type="button"
                    className="lp-mini-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      goToCustomerDiscovery();
                    }}
                  >
                    Order Now
                  </button>
                </div>
              </div>

              <div
                className="lp-mini-card"
                role="button"
                tabIndex={0}
                onClick={goToDineOutDiscovery}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") goToDineOutDiscovery();
                }}
              >
                <img
                  className="lp-mini-img"
                  src={LANDING_DINE_OUT_IMAGE}
                  alt="Dine Out"
                  loading="lazy"
                />
                <div className="lp-mini-overlay" />
                <div className="lp-mini-content">
                  <div>
                    <div className="lp-mini-title">Dine Out</div>
                    <p className="lp-mini-subtitle">Book a table &amp; enjoy great discounts &amp; offers</p>
                  </div>
                  <button
                    type="button"
                    className="lp-mini-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      goToDineOutDiscovery();
                    }}
                  >
                    Book Table
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section style={{ padding: "44px 0 0", background: "transparent" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 24,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "#1a1a1a",
                    margin: "0 0 4px",
                  }}
                >
                  What's on your mind?
                </h2>
                <p style={{ fontSize: 14, color: "#686b78", margin: 0 }}>
                  Browse by cuisine or craving
                </p>
              </div>
            </div>

            <div
              className="lp-cat-scroll"
              ref={categoriesRef}
              onWheel={handleCategoryWheel}
              style={{
                display: "flex",
                gap: 12,
                padding: "14px 12px",
                borderRadius: 22,
                background: "rgba(255,255,255,0.65)",
                border: "1px solid rgba(15,23,42,0.06)",
                boxShadow: "0 18px 50px rgba(15,23,42,0.10)",
              }}
            >
              {foodCategories.map((cat, index) => (
                <button
                  key={cat.id}
                  className="lp-cat-chip"
                  onClick={() => browseWithSearch(cat.label)}
                  style={{
                    background: "rgba(255,255,255,0.7)",
                    borderRadius: 18,
                    padding: "12px 12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 108,
                    border: "1px solid rgba(15,23,42,0.06)",
                    cursor: "pointer",
                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 18,
                      overflow: "hidden",
                      boxShadow: "0 10px 22px rgba(15,23,42,0.10)",
                      background: cat.bg,
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <img
                      src={cat.image}
                      alt={cat.label}
                      loading="lazy"
                      onError={(event) => {
                        const fallback =
                          DEFAULT_DYNAMIC_FOOD_CATEGORIES[index % DEFAULT_DYNAMIC_FOOD_CATEGORIES.length]?.image ||
                          FALLBACK_IMAGE;
                        if (event.currentTarget.src !== fallback) {
                          event.currentTarget.src = fallback;
                        }
                      }}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#3d4152",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── RESTAURANT GRID ─────────────────────────────────────── */}
	        <section style={{ padding: "48px 0", background: "transparent" }}>
	          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
            {/* Section header */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                marginBottom: 28,
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      width: 4,
                      height: 22,
                      background: "linear-gradient(#FC8019,#E84545)",
                      borderRadius: 4,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#FC8019",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Top Picks
                  </span>
                </div>
                <h2
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: "#1a1a1a",
                    margin: 0,
                  }}
                >
                  Popular restaurants near you
                </h2>
              </div>
		              {hasSelectedLocation && restaurants.length > 0 && (
		                <button
		                  className="lp-btn-outline"
		                  onClick={goToCustomerDiscovery}
		                  style={{
		                    padding: "9px 20px",
		                    borderRadius: 12,
	                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
	                  See all restaurants <ArrowRight size={14} />
	                </button>
	              )}
            </div>

            {/* Cards grid */}
	            {!hasSelectedLocation ? (
	              <div
	                style={{
	                  border: "1.5px dashed #f3c8a4",
	                  borderRadius: 24,
                  padding: "44px 28px",
                  background: "linear-gradient(135deg, #fffaf5 0%, #fff 100%)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 18,
                    margin: "0 auto 18px",
                    background: "#fff",
                    boxShadow: "0 8px 24px rgba(252,128,25,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MapPin size={26} color="#FC8019" />
                </div>
                <h3
                  style={{
                    margin: "0 0 8px",
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#1a1a1a",
                  }}
                >
                  Set your location
                </h3>
                <p
                  style={{
                    margin: "0 auto 18px",
                    maxWidth: 480,
                    color: "#686b78",
                    lineHeight: 1.6,
                  }}
                >
                  Select your location or use your current location to explore restaurants near you within {NEARBY_RADIUS_KM} km.
	                </p>
	                <button
	                  className="lp-btn-primary"
	                  onClick={() => detectAndSelectCurrentLocation({ closeModal: false })}
	                  style={{
	                    padding: "12px 26px",
	                    borderRadius: 14,
	                    fontSize: 14,
	                    display: "inline-flex",
	                    alignItems: "center",
	                    gap: 8,
	                  }}
	                >
	                  Use My Location
	                  <ArrowRight size={16} />
	                </button>
                  {locationAccessError && (
                    <p style={{ margin: "14px auto 0", maxWidth: 560, color: "#9CA3AF", fontSize: 12, lineHeight: 1.5 }}>
                      {locationAccessError}
                    </p>
                  )}
	              </div>
	            ) : isLoading ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 20,
                }}
              >
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#fff",
                      borderRadius: 20,
                      overflow: "hidden",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      style={{
                        height: 180,
                        background:
                          "linear-gradient(90deg, #f5f5f5 25%, #eee 37%, #f5f5f5 63%)",
                        backgroundSize: "400px 100%",
                        animation: "lpShimmer 1.4s infinite",
                      }}
                    />
                    <div style={{ padding: 16 }}>
                      <div
                        style={{
                          height: 18,
                          background: "#f5f5f5",
                          borderRadius: 6,
                          marginBottom: 8,
                          width: "70%",
                        }}
                      />
                      <div
                        style={{
                          height: 14,
                          background: "#f5f5f5",
                          borderRadius: 6,
                          width: "50%",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : restaurants.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 20,
                }}
              >
                {enriched.map((r, i) => (
                  <RestaurantCard key={r.id} restaurant={r} index={i} onClick={browseRestaurant} />
                ))}
              </div>
            ) : (
              <div
                style={{
                  borderRadius: 24,
                  padding: "40px 28px",
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  textAlign: "center",
                }}
              >
                <Navigation size={26} color="#FC8019" style={{ marginBottom: 14 }} />
                <h3
                  style={{
                    margin: "0 0 8px",
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#1a1a1a",
                  }}
                >
                  No nearby restaurants found
                </h3>
                <p style={{ margin: 0, color: "#686b78", lineHeight: 1.6 }}>
                  We couldn&apos;t find active restaurants within {NEARBY_RADIUS_KM} km of the selected location.
                </p>
              </div>
            )}

            {/* Load more CTA */}
		            {!isLoading && hasSelectedLocation && restaurants.length > 0 && (
		              <div style={{ textAlign: "center", marginTop: 36 }}>
		                <button
		                  className="lp-btn-primary"
		                  onClick={goToCustomerDiscovery}
		                  style={{
		                    padding: "13px 36px",
		                    borderRadius: 14,
	                    fontSize: 15,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
	                >
	                  Explore all restaurants
	                  <ArrowRight size={17} />
	                </button>
	              </div>
	            )}
          </div>
        </section>

        {/* ── FEATURES ────────────────────────────────────────────── */}
        <section
          style={{
            padding: "64px 0",
            background: "#fafafa",
            borderTop: "1px solid #f2f2f2",
          }}
        >
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#FC8019",
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Why FoodHub
              </p>
              <h2
                style={{
                  fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  fontSize: "clamp(26px, 4vw, 38px)",
                  fontWeight: 800,
                  color: "#1a1a1a",
                  margin: 0,
                }}
              >
                Better food experience,
                <br />
                every single time
              </h2>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 20,
              }}
            >
              {FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  className="lp-feature-card"
                  style={{
                    background: "#fff",
                    borderRadius: 20,
                    padding: "28px 24px",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                    border: "1.5px solid #f5f5f5",
                    animationDelay: `${i * 80}ms`,
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 16,
                      background: f.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 18,
                    }}
                  >
                    <f.icon size={24} color={f.color} />
                  </div>
                  <h3
                    style={{
                      fontSize: 17,
                      fontWeight: 800,
                      color: "#1a1a1a",
                      margin: "0 0 8px",
                    }}
                  >
                    {f.title}
                  </h3>
                  <p
                    style={{
                      fontSize: 14,
                      color: "#686b78",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ────────────────────────────────────────── */}
        <section style={{ padding: "64px 0", background: "transparent" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#FC8019",
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Simple & Fast
              </p>
              <h2
                style={{
                  fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  fontSize: "clamp(26px, 4vw, 38px)",
                  fontWeight: 800,
                  color: "#1a1a1a",
                  margin: 0,
                }}
              >
                How it works
              </h2>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 8,
                position: "relative",
              }}
            >
              {[
                {
                  step: "01",
                  icon: "📍",
                  title: "Set your location",
                  desc: "Enter your delivery address to see nearby restaurants.",
                },
                {
                  step: "02",
                  icon: "🍽️",
                  title: "Pick your food",
                  desc: "Browse menus, add items to cart & customize your order.",
                },
                {
                  step: "03",
                  icon: "💳",
                  title: "Pay securely",
                  desc: "Multiple payment options: UPI, card, cash on delivery.",
                },
                {
                  step: "04",
                  icon: "🚀",
                  title: "Get it delivered",
                  desc: "Track your order in real-time & enjoy hot food at home.",
                },
              ].map((step, i) => (
                <div
                  key={step.step}
                  style={{
                    textAlign: "center",
                    padding: "28px 20px",
                    position: "relative",
                  }}
                >
                  {/* Connector line */}
                  {i < 3 && (
                    <div
                      style={{
                        position: "absolute",
                        top: 44,
                        right: -16,
                        width: 32,
                        height: 2,
                        background: "linear-gradient(90deg,#FC8019,#fde0c4)",
                        zIndex: 1,
                        display: "none",
                      }}
                      className="step-connector"
                    />
                  )}

                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 24,
                      background: "linear-gradient(135deg,#fff9f4,#fde0c4)",
                      border: "2px solid #FC8019",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                      fontSize: 32,
                      position: "relative",
                    }}
                  >
                    {step.icon}
                    <div
                      style={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "#FC8019",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 800,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {step.step}
                    </div>
                  </div>
                  <h3
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: "#1a1a1a",
                      margin: "0 0 8px",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#686b78",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA BANNER ──────────────────────────────────────────── */}
        <section style={{ padding: "0 24px 64px", background: "transparent" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div
              style={{
                background:
                  "linear-gradient(135deg,#1a1a2e 0%,#7c2d12 50%,#ea580c 100%)",
                borderRadius: 28,
                padding: "clamp(32px, 5vw, 56px) clamp(24px, 5vw, 56px)",
                display: "flex",
                flexWrap: "wrap",
                gap: 28,
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 20px 60px rgba(124,45,18,0.3)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative circles */}
              <div
                style={{
                  position: "absolute",
                  width: 300,
                  height: 300,
                  borderRadius: "50%",
                  border: "40px solid rgba(255,255,255,0.04)",
                  top: -100,
                  right: -50,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  border: "30px solid rgba(255,255,255,0.04)",
                  bottom: -60,
                  left: 200,
                  pointerEvents: "none",
                }}
              />

              <div style={{ maxWidth: 520, position: "relative" }}>
                <p
                  style={{
                    fontSize: 13,
                    color: "#fde0c4",
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    marginBottom: 10,
                  }}
                >
                  Ready to start?
                </p>
                <h2
                  style={{
                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                    fontSize: "clamp(24px, 4vw, 38px)",
                    fontWeight: 800,
                    color: "#fff",
                    margin: "0 0 14px",
                    lineHeight: 1.2,
                  }}
                >
                  Join thousands of happy customers already ordering on FoodHub!
                </h2>
                <p
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 15,
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  Sign up now and get your first 3 deliveries free. No hidden
                  charges, cancel anytime.
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  position: "relative",
                }}
              >
                <button
                  onClick={onSignupClick}
                  style={{
                    background: "#fff",
                    color: "#FC8019",
                    border: "none",
                    borderRadius: 16,
                    padding: "14px 28px",
                    fontSize: 15,
                    fontWeight: 800,
                    cursor: "pointer",
                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                    transition: "transform 0.18s ease",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform =
                      "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform =
                      "translateY(0)";
                  }}
                >
                  Create free account
                  <ArrowRight size={17} />
                </button>
                <button
                  onClick={onSignInClick}
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderRadius: 16,
                    padding: "14px 28px",
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                    backdropFilter: "blur(8px)",
                    transition: "background 0.18s ease",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      "rgba(255,255,255,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      "rgba(255,255,255,0.12)";
                  }}
                >
                  Already a member?
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── FOOTER ──────────────────────────────────────────────── */}
        <footer style={{ background: "transparent", padding: "40px 24px 28px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 32,
                justifyContent: "space-between",
                marginBottom: 32,
              }}
            >
              {/* Brand */}
              <div style={{ maxWidth: 280 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "linear-gradient(135deg,#FC8019,#E84545)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Utensils size={17} color="#fff" />
                  </div>
                  <span
                    style={{
                      fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                      fontSize: 20,
                      fontWeight: 800,
                      color: "#111827",
                    }}
                  >
                    FoodHub
                  </span>
                </div>
                <p
                  style={{
                    color: "#6b7280",
                    fontSize: 13,
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  Your go-to food delivery app. Bringing the best meals from top
                  restaurants straight to your door.
                </p>
              </div>

              {/* Links */}
              {[
                {
                  heading: "Company",
                  links: ["About Us", "Careers", "Blog", "Press"],
                },
                {
                  heading: "For You",
                  links: ["Restaurants", "Offers", "Gift Cards", "Support"],
                },
                {
                  heading: "Legal",
                  links: ["Privacy Policy", "Terms of Use", "Cookie Policy"],
                },
              ].map((col) => (
                <div key={col.heading}>
                  <p
                    style={{
                      color: "#111827",
                      fontWeight: 700,
                      fontSize: 14,
                      marginBottom: 12,
                      margin: "0 0 12px",
                    }}
                  >
                    {col.heading}
                  </p>
                  {col.links.map((link) => (
                    <p
                      key={link}
                      style={{
                        color: "#6b7280",
                        fontSize: 13,
                        margin: "0 0 9px",
                        cursor: "pointer",
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.color =
                          "#FC8019";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.color =
                          "#6b7280";
                      }}
                    >
                      {link}
                    </p>
                  ))}
                </div>
              ))}
            </div>

            {/* Bottom bar */}
            <div
              style={{
                borderTop: "1px solid rgba(15,23,42,0.10)",
                paddingTop: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <p
                style={{
                  color: "#9ca3af",
                  fontSize: 12,
                  margin: 0,
                }}
              >
                © 2025 FoodHub. Made with ❤️ in India.
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                {["📱 App Store", "▶ Play Store"].map((label) => (
                  <button
                    key={label}
                    onClick={onSignupClick}
                    style={{
                      background: "rgba(252,128,25,0.10)",
                      color: "#7c2d12",
                      border: "1px solid rgba(252,128,25,0.18)",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* ── LOCATION SELECTION MODAL (Dine-Out style) ─────────────────────── */}
      {showLocationModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowLocationModal(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 480,
              maxHeight: "85vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px 16px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#1a1a1a",
                  margin: 0,
                  fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                }}
              >
                Select Location
              </h2>
              <button
                type="button"
                onClick={() => setShowLocationModal(false)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: "#f5f5f5",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.16s",
                }}
              >
                <X size={18} color="#555" />
              </button>
            </div>

            {/* Modal Scrollable Body */}
	            <div
	              style={{
	                padding: "20px 24px",
	                overflowY: "auto",
	                flex: 1,
	              }}
	            >
		              {/* Use Current Location Button */}
		              <button
		                type="button"
		                onClick={handleUseCurrentLocation}
                disabled={detectingLocation}
                style={{
                  width: "100%",
                  background: detectingLocation
                    ? "#ccc"
                    : "linear-gradient(135deg, #FC8019 0%, #E84545 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 16,
                  padding: "16px 24px",
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: detectingLocation ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  transition: "transform 0.18s, box-shadow 0.18s",
                  boxShadow: detectingLocation
                    ? "none"
                    : "0 6px 20px rgba(252,128,25,0.35)",
                }}
              >
                <Navigation
                  size={20}
                  style={{
                    animation: detectingLocation ? "spin 1s linear infinite" : "none",
                  }}
                />
                {detectingLocation ? "Detecting location…" : "Use Current Location"}
              </button>

              {/* Divider */}
              {/* <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "20px 0",
                }}
              >
                <div style={{ flex: 1, height: 1, background: "#e8e8e8" }} />
                <span style={{ fontSize: 13, color: "#999", fontWeight: 600, fontFamily: "'Manrope', 'Segoe UI', sans-serif" }}>OR</span>
                <div style={{ flex: 1, height: 1, background: "#e8e8e8" }} />
              </div>

              // Saved Addresses header
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 14,
                }}
              >
                <MapPin size={16} color="#7C4DFF" />
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#1a1a1a",
                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  }}
                >
                  Saved Addresses
                </span>
              </div>

              {loadingAddresses ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px 0",
                    color: "#999",
                    fontSize: 14,
                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  }}
                >
                  Loading addresses…
                </div>
              ) : savedAddresses.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px 16px",
                    background: "#fafafa",
                    borderRadius: 14,
                    color: "#999",
                    fontSize: 14,
                    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  }}
                >
                  No saved addresses found.
                  <br />
                  <button
                    type="button"
                    onClick={onSignInClick}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#FC8019",
                      fontWeight: 700,
                      cursor: "pointer",
                      fontSize: 14,
                      fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                      marginTop: 4,
                      textDecoration: "underline",
                    }}
                  >
                    Sign in to see your addresses.
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {savedAddresses.map((addr) => {
                    const icons: Record<string, React.ReactNode> = {
                      Home: <Home size={18} color="#3B82F6" />,
                      Work: <Briefcase size={18} color="#8B5CF6" />,
                      Other: <MapPin size={18} color="#EC4899" />,
                    };
                    return (
                      <div
                        key={addr.id}
                        style={{
                          background: "#fff",
                          border: "1.5px solid #f0f0f0",
                          borderRadius: 14,
                          padding: "14px 16px",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                          cursor: "pointer",
                          transition: "border-color 0.16s, box-shadow 0.16s",
                        }}
                        onClick={() => handleSelectSavedAddress(addr)}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.borderColor = "#FC8019";
                          (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 16px rgba(252,128,25,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.borderColor = "#f0f0f0";
                          (e.currentTarget as HTMLElement).style.boxShadow = "none";
                        }}
                      >
                        <div
                          style={{
                            width: 38, height: 38, borderRadius: 10,
                            background: addr.type === "Home" ? "#EFF6FF" : addr.type === "Work" ? "#F5F3FF" : "#FDF2F8",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          }}
                        >
                          {icons[addr.type] || icons["Other"]}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#1a1a1a", marginBottom: 4, fontFamily: "'Manrope', 'Segoe UI', sans-serif" }}>
                            {addr.type}
                          </div>
                          <div style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
                            {addr.address}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            style={{ width: 32, height: 32, borderRadius: "50%", background: "#EFF6FF", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <Edit size={14} color="#3B82F6" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setSavedAddresses((prev) => prev.filter((a) => a.id !== addr.id))}
                            style={{ width: 32, height: 32, borderRadius: "50%", background: "#FEF2F2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <Trash2 size={14} color="#EF4444" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add New Address button */}
              <button
                type="button"
                onClick={onSignInClick}
                style={{
                  width: "100%",
                  marginTop: 16,
                  background: "transparent",
                  border: "1.5px dashed #FC8019",
                  borderRadius: 14,
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  cursor: "pointer",
                  color: "#FC8019",
                  fontSize: 15,
                  fontWeight: 700,
                  fontFamily: "'Manrope', 'Segoe UI', sans-serif",
                  transition: "background 0.16s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#FFF7F0"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <Plus size={18} color="#FC8019" />
                Add New Address
              </button>

              {/* Spin animation for detecting */}
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
